import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import './Auth.css';
import '../../pages/Pages.css';
import artfooter from '../home/image/artfooter.png';

const Register = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [formError, setFormError] = useState('');
  const { register, loading, error, currentUser } = useAuth();
  const navigate = useNavigate();
  const recaptcha = useRef();

  // Redirect if already logged in
  useEffect(() => {
    if (currentUser) {
      navigate('/profile');
    }
  }, [currentUser, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    const { firstName, lastName, email, password, confirmPassword } = formData;
    
    // Form validation
    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      setFormError(t('please_fill_all_fields'));
      return;
    }
    
    if (password !== confirmPassword) {
      setFormError(t('passwords_dont_match'));
      return;
    }
    
    if (password.length < 6) {
      setFormError(t('password_min_length'));
      return;
    }

    // Verify reCAPTCHA
    const captchaValue = recaptcha.current.getValue();
    if (!captchaValue) {
      setFormError(t('recaptcha_not_ready'));
      return;
    }
    
    try {
      // Combine first name and last name for the backend's 'name' field
      const fullName = `${firstName} ${lastName}`;
      const response = await register(email, password, fullName, captchaValue);
      
      // Normal successful registration
      navigate('/login', { state: { registered: true, message: response.message } });
    } catch (err) {
      console.error('Registration error:', err);
      if (err.response?.data?.error?.code === 'RECAPTCHA_FAILED') {
        setFormError(t('recaptcha_verification_failed'));
      } else if (err.response?.data?.code === 'GUEST_ACCOUNT_EXISTS') {
        // Guest account exists case - show the message from the backend
        setFormError(err.response.data.error);
      } else {
        setFormError(err.response?.data?.error || err.message || t('registration_error'));
      }
    }
  };

  // Don't render the form if already logged in
  if (currentUser) {
    return null;
  }

  return (
    <div className="auth-container">
      <div className="auth-card register-card">
        <div className="logo-container logo-ellipse">
          <img src={artfooter} alt={t('studio_name')} className="footer-logo-img" />
        </div>
        
        <p className="welcome-text">{t('join_studio')}</p>
        
        <h2>{t('register')}</h2>
        
        {(formError || error) && (
          <div className="error-message">
            {formError || error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">{t('first_name')}</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder={t('enter_first_name')}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="lastName">{t('last_name')}</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder={t('enter_last_name')}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="email">{t('email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('enter_your_email')}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">{t('password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t('enter_your_password')}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">{t('confirm_password')}</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t('confirm_your_password')}
              required
            />
          </div>
          
          <div className="form-group recaptcha-container">
            <ReCAPTCHA
              ref={recaptcha}
              sitekey="6LdfTT8rAAAAAM_TkD9dKutCyy3LJ4KHyINaRWtI"
            />
          </div>
          
          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? t('registering') : t('register')}
          </button>
        </form>
        
        <p className="auth-link">
          {t('already_have_account')} <Link to="/login">{t('login_here')}</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
